import { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { BlockOutlined } from "@mui/icons-material";
import { Chip, IconButton, MenuItem, Select, SelectChangeEvent } from "@mui/material";

import { Data } from "../types";

import { TableWrapperStyle } from "./style";
import headCells from "./tableHeadCells";

import TableSkeleton from "@/components/app/TableSkeleton";
import { getComparator, Order } from "@/utils/table-utils";
import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import ConfirmationModal from "@/components/app/modalVariants/confirmation";
import { useRemoveInstitutionDevice, useUpdateInstitutionDeviceStatus } from "@/api/hooks/institution";
import { InstitutionDeviceStatus } from "@/api/types/institution";
import Loading from "@/components/app/Loading/tableLoading";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";

export default function InstitutionDevicesTable({
    data: rows,
    institutionId,
    isLoading,
    isFetching,
}: {
    data: Data[],
    institutionId: number,
    isLoading: boolean,
    isFetching: boolean,
}) {
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof Data>("createdAt");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [revokeModalId, setRevokeModal] = useState<number | null>(null);

    const updateAccess = useCheckPermissionAccess(PagesAccess.INSTITUTION, OperationAccess.UPDATE);
    const deleteAccess = useCheckPermissionAccess(PagesAccess.INSTITUTION, OperationAccess.DELETE);

    const { mutate: updateDeviceStatus } = useUpdateInstitutionDeviceStatus();
    const { mutate: removeDevice, isPending: isRevoking } = useRemoveInstitutionDevice();

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleStatusChange = (deviceId: number, event: SelectChangeEvent) => {
        updateDeviceStatus({
            id: institutionId,
            deviceId,
            body: { status: event.target.value as InstitutionDeviceStatus },
        });
    };

    const handleClose = () => setRevokeModal(null);
    const handleRevoke = () =>
        revokeModalId &&
        removeDevice(
            { id: institutionId, deviceId: revokeModalId },
            {
                onSuccess: () => setRevokeModal(null),
            }
        );

    const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows]);
    const paginatedRows = useMemo(
        () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [visibleRows, page, rowsPerPage]
    );

    return (
        <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "1em" }}>
            <TableContainer sx={{ maxHeight: "700px" }}>
                <Table
                    stickyHeader
                    aria-labelledby="tableTitle"
                    className="table"
                    component="div"
                    size="medium"
                    sx={{ padding: ".5em" }}
                >
                    <EnhancedTableHead<Data>
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div" sx={{ position: "relative" }}>
                        {isLoading && <TableSkeleton cells={headCells.map((item) => item.id)} />}
                        {!isLoading && !isFetching && paginatedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headCells.length + 1}>No Devices To Show</TableCell>
                            </TableRow>
                        )}
                        {isFetching && <Loading />}
                        {paginatedRows.map((row) => {
                            return (
                                <TableRow key={row.id} className="table-row" component="div" role="checkbox" tabIndex={-1}>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.id}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div" sx={{ width: "20%" }}>
                                        <span className="table-value">{row.deviceUid}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div" sx={{ width: "20%" }}>
                                        <span className="table-value">{row.deviceName}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div" sx={{ width: "15%" }}>
                                        <span className="table-value">
                                            {updateAccess ? (
                                                <Select
                                                    fullWidth
                                                    size="small"
                                                    value={row.status}
                                                    onChange={(e) => handleStatusChange(row.id, e)}
                                                >
                                                    <MenuItem value="active">Active</MenuItem>
                                                    <MenuItem value="suspended">Suspended</MenuItem>
                                                </Select>
                                            ) : (
                                                <Chip
                                                    color={row.status === "active" ? "success" : "warning"}
                                                    label={row.status}
                                                    size="small"
                                                />
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.lastSeenAt}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.createdAt}</span>
                                    </TableCell>
                                    <TableCell align="right" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "5px", justifyContent: "flex-end" }}>
                                            <IconButton
                                                disabled={!deleteAccess}
                                                title="Revoke"
                                                onClick={() => setRevokeModal(row.id)}
                                            >
                                                <BlockOutlined />
                                            </IconButton>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={visibleRows.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <ConfirmationModal
                description="Are you sure you want to revoke this device? It will lose access immediately."
                handleClose={handleClose}
                loading={isRevoking}
                open={Boolean(revokeModalId)}
                title="Revoke Device"
                onConfirm={handleRevoke}
            />
        </TableWrapperStyle>
    );
}
